import React, { useState, useEffect } from 'react';
import './ColumnMappingModal.css';

function ColumnMappingModal({
  isOpen,
  onClose,
  previewData,
  onConfirm,
  isLoading
}) {
  const [mappings, setMappings] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    if (previewData?.suggestedMappings) {
      // Initialize with suggested mappings
      const initialMappings = {};
      Object.entries(previewData.suggestedMappings).forEach(([index, fieldKey]) => {
        initialMappings[index] = fieldKey;
      });
      setMappings(initialMappings);
    }
  }, [previewData]);

  useEffect(() => {
    // Validate required fields are mapped
    const errors = [];
    const requiredFields = previewData?.systemFields?.filter(f => f.required) || [];
    const mappedFields = Object.values(mappings);

    requiredFields.forEach(field => {
      if (!mappedFields.includes(field.key)) {
        errors.push(`"${field.label}" is required but not mapped`);
      }
    });

    setValidationErrors(errors);
  }, [mappings, previewData]);

  const handleMappingChange = (columnIndex, fieldKey) => {
    setMappings(prev => {
      const newMappings = { ...prev };
      if (fieldKey === '' || fieldKey === 'skip') {
        delete newMappings[columnIndex];
      } else {
        newMappings[columnIndex] = fieldKey;
      }
      return newMappings;
    });
  };

  const handleConfirm = () => {
    if (validationErrors.length > 0) {
      return;
    }
    onConfirm(mappings);
  };

  const getFieldLabel = (fieldKey) => {
    const field = previewData?.systemFields?.find(f => f.key === fieldKey);
    return field?.label || fieldKey;
  };

  const isFieldMapped = (fieldKey) => {
    return Object.values(mappings).includes(fieldKey);
  };

  if (!isOpen || !previewData) return null;

  return (
    <div className="modal-overlay">
      <div className="column-mapping-modal">
        <div className="modal-header">
          <h2>Map Excel Columns</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="mapping-info">
            <p>
              <strong>File:</strong> {previewData.sheetName} ({previewData.totalRows} rows)
            </p>
            <p className="mapping-hint">
              Map each Excel column to the corresponding database field.
              Required fields are marked with <span className="required-marker">*</span>
            </p>
          </div>

          {validationErrors.length > 0 && (
            <div className="validation-errors">
              <strong>Please fix the following:</strong>
              <ul>
                {validationErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mapping-table-container">
            <table className="mapping-table">
              <thead>
                <tr>
                  <th>Excel Column</th>
                  <th>Sample Values</th>
                  <th>Map To Field</th>
                </tr>
              </thead>
              <tbody>
                {previewData.columns?.map((column) => (
                  <tr key={column.index} className={mappings[column.index] ? 'mapped' : ''}>
                    <td className="column-header">
                      <strong>{column.header || `Column ${column.index + 1}`}</strong>
                    </td>
                    <td className="sample-values">
                      {column.sampleValues?.slice(0, 3).map((val, idx) => (
                        <span key={idx} className="sample-value">
                          {val || <em className="empty-value">(empty)</em>}
                        </span>
                      ))}
                    </td>
                    <td className="mapping-select">
                      <select
                        value={mappings[column.index] || ''}
                        onChange={(e) => handleMappingChange(column.index, e.target.value)}
                      >
                        <option value="">-- Skip Column --</option>
                        {previewData.systemFields?.map((field) => (
                          <option
                            key={field.key}
                            value={field.key}
                            disabled={field.key !== 'skip' && isFieldMapped(field.key) && mappings[column.index] !== field.key}
                          >
                            {field.label} {field.required && '*'}
                            {isFieldMapped(field.key) && mappings[column.index] !== field.key && ' (already mapped)'}
                          </option>
                        ))}
                      </select>
                      {column.suggestedMapping && mappings[column.index] !== column.suggestedMapping && (
                        <button
                          className="suggestion-btn"
                          onClick={() => handleMappingChange(column.index, column.suggestedMapping)}
                          title={`Auto-detected: ${getFieldLabel(column.suggestedMapping)}`}
                        >
                          Use suggestion
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mapping-summary">
            <h4>Mapping Summary</h4>
            <div className="summary-grid">
              {previewData.systemFields?.filter(f => f.key !== 'skip').map((field) => (
                <div
                  key={field.key}
                  className={`summary-item ${isFieldMapped(field.key) ? 'mapped' : 'unmapped'} ${field.required ? 'required' : ''}`}
                >
                  <span className="field-name">
                    {field.label} {field.required && <span className="required-marker">*</span>}
                  </span>
                  <span className="field-status">
                    {isFieldMapped(field.key) ? 'Mapped' : 'Not mapped'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={validationErrors.length > 0 || isLoading}
          >
            {isLoading ? 'Importing...' : 'Confirm & Import'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ColumnMappingModal;
